import type { IReportCampaign } from '~/modules/campaign-analytics/types';
import type { EReportSource } from '~/modules/report-analytic/types';
import type { IOrderPageAnalytic } from '~/modules/order-analytic';
import type { EAnalyticMode } from '~/modules/analytics/types';

import { GModalOrderJourney as SdkGModalOrderJourney } from '@gem-sdk/analytics';
import { useQueryOrderJourney } from '~/modules/order-analytic/api';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { useGModalContext, ModalType } from '~/presentation/ui';
import { AnalyticsSdkProvider } from '~/modules/analytics';
import { useCallback, type ComponentProps } from 'react';

// TEMP: the SDK's ICampaignType has no URL until @gem-sdk/analytics ships it on the 4.x staging line.
type SdkReportCampaign = ComponentProps<typeof SdkGModalOrderJourney>['reportCampaign'];

export interface GModalOrderJourneyProps {
  title: string;
  reportCampaign?: IReportCampaign;
  pageAnalytic?: IOrderPageAnalytic;
  mode: EAnalyticMode;
  journeySource?: EReportSource;
}

export const GModalOrderJourney = ({
  title,
  reportCampaign,
  pageAnalytic,
  mode,
  journeySource,
}: GModalOrderJourneyProps) => {
  const { isOpen, modalType, initialValues, onCloseModal } =
    useGModalContext<ModalType.ORDER_JOURNEY>();
  const { handleOpenPageShopifyInNewTab } = useLocationHandlers();

  const orderId = initialValues?.orderId ?? '';
  const gxId = initialValues?.gxId;
  const isOpenModalOrderJourney = isOpen && modalType === ModalType.ORDER_JOURNEY;

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useQueryOrderJourney({
    orderID: orderId,
  });

  const handleViewOrderDetails = useCallback(
    (id: string) => handleOpenPageShopifyInNewTab(`orders/${id}`),
    [handleOpenPageShopifyInNewTab],
  );

  return (
    <AnalyticsSdkProvider>
      <SdkGModalOrderJourney
        open={isOpenModalOrderJourney}
        onClose={onCloseModal}
        title={title}
        orderId={orderId}
        gxId={gxId}
        mode={mode}
        journeySource={journeySource}
        journeyItems={data}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetching}
        fetchNextPage={fetchNextPage}
        onViewOrderDetails={handleViewOrderDetails}
        reportCampaign={reportCampaign as SdkReportCampaign}
        pageAnalytic={pageAnalytic}
      />
    </AnalyticsSdkProvider>
  );
};
